import React, {
    createContext, useContext, useState, useRef, useEffect, useCallback, useMemo
} from 'react';

import { getSocket, onSocket } from '../utils/socket';
import { getIceConfig, getLocalStream, getDisplayStream, stopStream } from '../utils/webrtc';
import { createMediaRoom } from '../utils/callLiveKit';
import { createDenoisedTrack, isNoiseSuppressionSupported } from '../utils/noiseSuppression';
import { isAudioOutputSelectionSupported, listAudioOutputs } from '../utils/audioOutput';
import {
    unlockAudio, startRinging, stopRinging, flashTitle, stopFlashTitle,
    startRingback, stopRingback, playDisconnected,
    clearAttention, notify, requestNotificationPermission
} from '../utils/attention';

import CallWidget from '../components/call/CallWidget';
import CallWindow from '../components/call/CallWindow';
import CallAudioSinks from '../components/call/CallAudioSinks';

/**
 * Tell the server something went wrong on this side of a call, so the admin
 * usage report can show why calls fail. Best effort: never throws and never
 * affects the call. Detail is diagnostics only (connection state, candidate
 * tallies, network type) - no audio, no message content.
 */
const reportCallIssue = (callId, code, message, { fatal = false, detail = null } = {}) => {
    try {
        const conn = navigator.connection;
        getSocket()?.emit('call:issue', {
            callId: callId ? String(callId) : null,
            code,
            message: String(message || '').slice(0, 300),
            fatal,
            detail: {
                ...(detail || {}),
                network: conn ? { type: conn.effectiveType, downlink: conn.downlink, rtt: conn.rtt } : undefined,
                online: navigator.onLine,
                ua: navigator.userAgent
            }
        });
    } catch { /* reporting must never break a call */ }
};

/**
 * One call at a time, for the whole application.
 *
 * Mounted in DashboardLayout rather than on the chat page, and that placement
 * is the point: a call has to survive the user navigating to Tasks while it
 * rings, and it has to *arrive* while they are looking at Attendance. A
 * provider inside Chats.js would tear the connections down the moment they
 * clicked away, and would never ring at all for anyone not already sitting in
 * the chat module.
 *
 * The state machine is small and worth stating outright:
 *
 *   idle → outgoing   → connecting → active → idle     (we called)
 *   idle → incoming   → connecting → active → idle     (they called)
 *
 * Every path back to idle goes through teardown(), because every one of them
 * has to stop the same microphone, close the same connections and silence the
 * same ringtone.
 *
 * A call may have up to maxParticipants people in it. Their media goes
 * through the LiveKit media server - utils/callLiveKit.js owns that one
 * connection; this owns the roster, the lifecycle and what the user sees. The
 * roster is authoritative and comes from the server: `call:roster` replaces it
 * wholesale. Deriving it locally from joins and leaves would drift the
 * moment one event was missed, and a drifted roster is a person nobody can hear.
 *
 * How it looks is a separate, smaller state: a call in progress is either a
 * floating window (CallWindow — movable, resizable, never modal) or minimised
 * to the pill in the top-right corner (CallWidget). A ringing call is always
 * the corner card.
 */

const CallContext = createContext(null);

export const useCall = () => useContext(CallContext);

const IDLE = {
    status: 'idle',
    callId: null,
    conversationId: null,
    conversationTitle: '',
    isGroup: false,
    kind: 'audio',

    // Who rang us. Drives the incoming card only — once we are in the call,
    // the roster is what matters.
    from: null,

    /*
     * Everyone on the call, us included, in join order.
     *
     * This replaced a single `peer` object. There is no "the other person" once
     * a third can join, and keeping a derived alias for it would have
     * guaranteed that some surface went on reading it and showed one name for a
     * call with four people in it.
     */
    participants: [],

    isInitiator: false,

    /*
     * Where the people on the call type to each other: their direct chat while
     * it is two of them, then a "Call at …" group the server makes once a third
     * joins. Null until somebody has answered.
     */
    chatConversationId: null,
    chatIsCallGroup: false,

    answeredAt: null,
    endedReason: null,
    maxParticipants: 20
};

const IN_CALL = ['outgoing', 'connecting', 'active', 'reconnecting'];

/**
 * How long a call survives with no connection to the server.
 *
 * Matches RECONNECT_GRACE_MS in server/utils/callSignalling.js, which holds the
 * seat for the same period. Shorter and the tab gives up on a call the server
 * is still holding; longer and it sits on "Reconnecting..." after the seat has
 * gone.
 */
const RECONNECT_TIMEOUT_MS = 30 * 1000;

const storedUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user') || '{}') || {};
    } catch {
        return {};
    }
};

const myUserId = () => String(storedUser()._id || storedUser().id || '');

const DEVICE_LABELS = { pc: 'PC', phone: 'Phone' };

const NOISE_SUPPRESSION_KEY = 'gts:call:noiseSuppression';

const storedNoiseSuppressionPref = () => {
    try {
        return localStorage.getItem(NOISE_SUPPRESSION_KEY) !== 'off';
    } catch {
        return true;
    }
};

const AUDIO_OUTPUT_KEY = 'gts:call:audioOutput';

// Empty means "system default" — never a literal deviceId, so a device that
// no longer exists on this browser or this device can't get stuck as the
// stored choice.
const storedAudioOutputPref = () => {
    try {
        return localStorage.getItem(AUDIO_OUTPUT_KEY) || '';
    } catch {
        return '';
    }
};

/*
 * Our own name and picture, for our own tile.
 *
 * Taken from storage rather than waited for: the server sends an authoritative
 * roster moments later, but the window opens the instant Call is pressed, and
 * without this the caller watches their own tile sit blank until somebody
 * answers.
 */
const myPerson = () => {
    const me = storedUser();
    return {
        _id: myUserId(),
        name: me.name || 'You',
        profilePic: me.profilePic || '',
        jobTitle: me.jobTitle || ''
    };
};

export const CallProvider = ({ children }) => {
    const [call, setCallState] = useState(IDLE);
    const [micOn, setMicOn] = useState(true);
    // Read by publishMediaState, which must not be rebuilt every time the
    // microphone is toggled — it is captured by the screen-share callbacks.
    const micOnRef = useRef(true);

    const noiseSuppressionSupported = useMemo(() => isNoiseSuppressionSupported(), []);
    const [noiseSuppressionOn, setNoiseSuppressionOn] = useState(storedNoiseSuppressionPref);
    // Read by ensureAudioTrack/publishLocal, for the same reason as micOnRef.
    const noiseSuppressionOnRef = useRef(storedNoiseSuppressionPref());
    /*
     * The raw mic track (what toggleMic mutes) and, once built, its RNNoise
     * derivative. toggleMic only ever disables the raw one — that's fine
     * even when the derivative is what's actually being sent, because a
     * disabled MediaStreamTrack feeding a MediaStreamAudioSourceNode makes
     * the whole graph downstream of it emit silence too. See ensureAudioTrack.
     */
    const rawAudioTrackRef = useRef(null);
    const denoiserRef = useRef(null); // { rawTrack, track, dispose } | null

    const audioOutputSupported = useMemo(() => isAudioOutputSelectionSupported(), []);
    const [audioOutputId, setAudioOutputIdState] = useState(storedAudioOutputPref);
    const [audioOutputs, setAudioOutputs] = useState([]);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [view, setView] = useState('window');   // 'window' | 'minimized'

    /*
     * userId -> { audio, camera, screen }, each a MediaStream or undefined.
     *
     * Mirrored into a ref as well as state: the socket handlers below need to
     * read the *current* media to tear it down, and a closure over state would
     * see whatever it was when the handler was registered.
     */
    const [remoteMedia, setRemoteMedia] = useState({});
    const remoteMediaRef = useRef({});

    /*
     * The microphone and camera, in a ref AND in state.
     *
     * The ref is what the socket handlers read — a closure over state would see
     * whatever it was when the handler was registered. The state is what makes
     * the self tile appear when the camera opens, which a ref alone cannot do.
     */
    const localStreamRef = useRef(null);
    const [localStream, setLocalStream] = useState(null);

    // The screen being shared from this tab, if any. Kept apart from the camera
    // stream because it is a different device with a different lifetime: it can
    // be stopped from outside the page entirely (see startScreenShare).
    const screenStreamRef = useRef(null);
    const [screenStream, setScreenStream] = useState(null);

    const roomRef = useRef(null);

    /*
     * Getting the media back after the media server connection dropped while
     * the call itself is still on. See recoverMedia.
     */
    const mediaRecoveryRef = useRef(null);
    const recoverMediaRef = useRef(() => {});

    const callRef = useRef(IDLE);

    /**
     * Change the call, and make the change visible to socket handlers at once.
     *
     * Every handler below decides whether an event is for it by comparing
     * against callRef, and the ref used to be filled in by an effect — which
     * runs only after React has re-rendered. Socket.io delivers a burst of
     * events in one tick and React 18 batches the state updates inside them, so
     * the second event in a burst read a ref that still held the state from
     * before the first.
     *
     * That dropped real work. The server sends `call:ringing` and `call:roster`
     * back to back when a call is placed: the first sets the call id, the second
     * was discarded because the id it was checked against had not landed yet —
     * so whoever placed a group call watched an empty window while three phones
     * were ringing.
     *
     * Writing the ref here, before the state update is queued, makes the ref the
     * authoritative current value and the React state purely what gets painted.
     * The updater is given the ref rather than React's `prev` for the same
     * reason: within a burst, the ref is the newer of the two.
     */
    const setCall = useCallback((updater) => {
        const next = typeof updater === 'function' ? updater(callRef.current) : updater;
        // Updaters that decline to act return the object they were given.
        if (next === callRef.current) return;
        callRef.current = next;
        setCallState(next);
    }, []);

    /*
     * Our seat on the call - our user id, unless this is the second device we
     * are on it from, when the server gives it an id of its own (`<id>~2`). The
     * roster says which; see applyRoster.
     */
    const [me, setMe] = useState(myUserId);
    const meRef = useRef(me);
    const setSelfId = useCallback((id) => {
        if (meRef.current === id) return;
        meRef.current = id;
        setMe(id);
    }, []);

    // Joined from a second device, muted: say so once the server has seated us.
    const announceMediaRef = useRef(false);

    /* ---------------------------------------------------------------- *
     * Sending
     * ---------------------------------------------------------------- */

    const setMedia = useCallback((userId, updater) => {
        const next = {
            ...remoteMediaRef.current,
            [userId]: { ...(remoteMediaRef.current[userId] || {}), ...updater }
        };
        remoteMediaRef.current = next;
        setRemoteMedia(next);
    }, []);

    const dropMedia = useCallback((userId) => {
        const next = { ...remoteMediaRef.current };
        delete next[userId];
        remoteMediaRef.current = next;
        setRemoteMedia(next);
    }, []);

    const patchParticipant = useCallback((userId, patch) => {
        setCall((prev) => ({
            ...prev,
            participants: prev.participants.map((p) =>
                (p.userId === String(userId) ? { ...p, ...patch } : p))
        }));
    }, [setCall]);

    /* ---------------------------------------------------------------- *
     * The media server connection
     * ---------------------------------------------------------------- */

    /*
     * The call's connection to the LiveKit media server, made on first use and
     * dropped by teardown (utils/callLiveKit.js).
     */
    const mediaRoom = useCallback(() => {
        if (roomRef.current) return roomRef.current;

        roomRef.current = createMediaRoom({
            onRemoteTrack: (userId, { slot, stream }) => {
                setMedia(userId, { [slot]: stream });
            },
            onPeerState: (userId, state) => {
                patchParticipant(userId, { connectionState: state });
                // The clock starts when somebody can actually be heard.
                if (state === 'connected') {
                    setCall((prev) => (prev.answeredAt
                        ? prev
                        : { ...prev, status: 'active', answeredAt: Date.now() }));
                }
            },
            // The SDK has given up on this room. The call has not: see recoverMedia.
            onDisconnected: (message) => {
                console.warn(`[call] media server connection lost (${message})`);
                recoverMediaRef.current();
            }
        });
        return roomRef.current;
    }, [setMedia, patchParticipant, setCall]);

    /* ---------------------------------------------------------------- *
     * Teardown
     * ---------------------------------------------------------------- */

    const teardown = useCallback((patch = {}) => {
        // A hang-up during a reconnect must not be overtaken by the deadline
        // that was counting down to the same thing.
        clearTimeout(reconnectTimer.current);
        clearAttention();

        clearTimeout(mediaRecoveryRef.current?.timer);
        mediaRecoveryRef.current = null;
        roomRef.current?.closeAll();
        roomRef.current = null;

        stopStream(localStreamRef.current);
        localStreamRef.current = null;
        setLocalStream(null);

        denoiserRef.current?.dispose();
        denoiserRef.current = null;
        rawAudioTrackRef.current = null;

        // Without this the browser goes on telling the user their screen is
        // being shared, on a call that has ended.
        stopStream(screenStreamRef.current);
        screenStreamRef.current = null;
        setScreenStream(null);

        remoteMediaRef.current = {};
        setRemoteMedia({});
        setMicOn(true);
        micOnRef.current = true;
        announceMediaRef.current = false;
        setSelfId(myUserId());

        setCall({ ...IDLE, ...patch });
    }, [setCall, setSelfId]);

    // The media callbacks and recoverMedia are created before teardown exists;
    // this is the knot that ties them, rather than reordering useCallbacks.
    const reconnectTimer = useRef(null);
    const teardownRef = useRef(teardown);
    useEffect(() => { teardownRef.current = teardown; }, [teardown]);

    /*
     * The media server connection is gone and the call is not.
     *
     * The LiveKit SDK reconnects by itself, and when that fails it gives the
     * room up. That used to leave the call on the spot, so a network blip
     * hung up on everybody. Instead the room is thrown away (a closed room can
     * otherwise come back and take the seat - see callLiveKit.js), the server
     * is asked for a fresh token (call:livekit-refresh) and the call:livekit
     * handler joins with it. Checked every few seconds for RECONNECT_TIMEOUT_MS,
     * the time the server holds the seat, then given up for real.
     */
    const recoverMedia = useCallback(() => {
        const callId = callRef.current.callId;
        if (!callId || callRef.current.status === 'idle') return;

        roomRef.current?.closeAll();
        roomRef.current = null;

        const recovery = mediaRecoveryRef.current || { since: Date.now(), timer: null };
        mediaRecoveryRef.current = recovery;
        clearTimeout(recovery.timer);

        if (Date.now() - recovery.since > RECONNECT_TIMEOUT_MS) {
            mediaRecoveryRef.current = null;
            setError('Connection lost. The call has ended.');
            getSocket()?.emit('call:leave', { callId });
            teardownRef.current?.();
            return;
        }

        setCall((prev) => (prev.callId === callId && prev.status !== 'reconnecting' && prev.status !== 'idle'
            ? { ...prev, status: 'reconnecting' }
            : prev));
        getSocket()?.emit('call:livekit-refresh', { callId });

        const check = () => {
            if (mediaRecoveryRef.current !== recovery || callRef.current.callId !== callId) return;
            const room = roomRef.current;
            if (room?.isConnected()) {
                mediaRecoveryRef.current = null;
                return;
            }
            // A token arrived and a room is mid-connect: give it time, within the deadline.
            if (room && Date.now() - recovery.since <= RECONNECT_TIMEOUT_MS) {
                recovery.timer = setTimeout(check, 5000);
                return;
            }
            recoverMediaRef.current();
        };
        recovery.timer = setTimeout(check, 5000);
    }, [setCall]);
    useEffect(() => { recoverMediaRef.current = recoverMedia; }, [recoverMedia]);

    /* ---------------------------------------------------------------- *
     * Building the local media
     * ---------------------------------------------------------------- */

    const ensureLocalStream = useCallback(async (kind) => {
        if (localStreamRef.current) return localStreamRef.current;
        const stream = await getLocalStream({ video: kind === 'video' });
        localStreamRef.current = stream;
        setLocalStream(stream);
        return stream;
    }, []);

    /**
     * The track that actually goes out: the raw mic, or its RNNoise
     * derivative when noise suppression is on and supported.
     *
     * Rebuilds the derivative if the raw track changed underneath it (a new
     * call started) and otherwise reuses the one already built, so toggling
     * suppression on and off mid-call is cheap after the first switch. A
     * build failure (no AudioWorklet, wasm blocked, whatever) falls back to
     * the raw track rather than losing the call over an enhancement.
     */
    const ensureAudioTrack = useCallback(async () => {
        const raw = localStreamRef.current?.getAudioTracks()[0] || null;
        rawAudioTrackRef.current = raw;
        if (!raw) return null;

        if (!noiseSuppressionSupported || !noiseSuppressionOnRef.current) {
            return raw;
        }

        if (denoiserRef.current && denoiserRef.current.rawTrack !== raw) {
            denoiserRef.current.dispose();
            denoiserRef.current = null;
        }

        if (!denoiserRef.current) {
            try {
                const { track, dispose } = await createDenoisedTrack(raw);
                denoiserRef.current = { rawTrack: raw, track, dispose };
            } catch (err) {
                console.warn('[call] noise suppression unavailable, using raw mic:', err.message);
                return raw;
            }
        }

        return denoiserRef.current.track;
    }, [noiseSuppressionSupported]);

    /**
     * Hand the microphone (and camera) to every connection.
     *
     * Pure replaceTrack on slots that are already negotiated, so this cannot
     * fail a connection and needs nobody's agreement — see the slot contract in
     * utils/webrtc.js.
     */
    const publishLocal = useCallback(async () => {
        const stream = localStreamRef.current;
        if (!stream) return;
        await mediaRoom().setLocal({
            audio: await ensureAudioTrack(),
            camera: stream.getVideoTracks()[0] || null
        });
    }, [mediaRoom, ensureAudioTrack]);

    /**
     * Flip noise suppression on or off, live if a call is already up.
     *
     * The preference is remembered across calls (and reloads) rather than
     * reset each time, on the same logic as any other call setting a person
     * sets once and expects to stick.
     */
    const toggleNoiseSuppression = useCallback(() => {
        if (!noiseSuppressionSupported) return;
        const next = !noiseSuppressionOnRef.current;
        noiseSuppressionOnRef.current = next;
        setNoiseSuppressionOn(next);
        try { localStorage.setItem(NOISE_SUPPRESSION_KEY, next ? 'on' : 'off'); } catch { /* private mode */ }

        if (!callRef.current.callId) return;
        (async () => {
            const track = await ensureAudioTrack();
            await roomRef.current?.setLocal({ audio: track });
        })();
    }, [noiseSuppressionSupported, ensureAudioTrack]);

    /* ---------------------------------------------------------------- *
     * Roster
     * ---------------------------------------------------------------- */

    /**
     * Take the server's roster as the truth, and make the peers match.
     *
     * Nobody is offered to from here. A roster is a snapshot and may arrive at
     * any time — when we join, when somebody else joins, when somebody is
     * invited — so acting on it by offering would produce a second offer for a
     * connection that already exists. The instruction to offer is edge-
     * triggered and arrives on `call:participant-joined` alone.
     */
    const applyRoster = useCallback((payload) => {
        const rows = payload.participants || [];
        const joined = rows.filter((p) => p.state === 'joined').map((p) => p.userId);

        // Which seat is ours. Our other device, if any, is never in the list.
        const self = String(payload.you?.userId || myUserId());
        setSelfId(self);
        const selfLabel = payload.you?.otherDevice
            ? (DEVICE_LABELS[payload.you?.device] || DEVICE_LABELS.pc)
            : '';

        // Anyone whose media we still hold who is no longer on the roster.
        for (const id of Object.keys(remoteMediaRef.current)) {
            if (!joined.includes(id)) dropMedia(id);
        }

        setCall((prev) => ({
            ...prev,
            callId: payload.callId,
            kind: payload.kind || prev.kind,
            isGroup: payload.isGroup ?? prev.isGroup,
            conversationTitle: payload.conversationTitle || prev.conversationTitle,
            chatConversationId: payload.chatConversationId || prev.chatConversationId,
            maxParticipants: payload.maxParticipants || prev.maxParticipants,
            participants: rows.map((row) => {
                const existing = prev.participants.find((p) => p.userId === row.userId);
                return {
                    ...row,
                    isSelf: row.userId === self,
                    // "You (PC)" while we are on the call from two devices.
                    deviceLabel: row.userId === self ? selfLabel : '',
                    connectionState: existing?.connectionState || '',
                    micOn: existing?.micOn ?? true,
                    camOn: existing?.camOn ?? false,
                    // The server's roster is authoritative about who holds the
                    // screen; it is the only thing that knows across a reconnect.
                    sharingScreen: Boolean(row.sharingScreen),
                    failure: existing?.failure || ''
                };
            })
        }));
    }, [setSelfId, dropMedia, setCall]);

    /* ---------------------------------------------------------------- *
     * Actions
     * ---------------------------------------------------------------- */

    /**
     * Place a call.
     *
     * The microphone is opened *before* the server is told to ring anybody.
     * Asking other people to pick up and only then discovering that this
     * browser has no microphone permission wastes their time and looks like a
     * fault at their end.
     */
    const startCall = useCallback(async (conversation, kind = 'audio', userIds = null) => {
        if (callRef.current.status !== 'idle') return;
        if (!conversation?._id) {
            setError('That conversation cannot be called.');
            return;
        }

        const isGroup = conversation.kind === 'group' || Boolean(conversation.isGroup);
        const other = conversation.otherUser;

        setError('');
        setNotice('');
        setView('window');
        setCall({
            ...IDLE,
            status: 'outgoing',
            conversationId: String(conversation._id),
            kind,
            isGroup,
            conversationTitle: isGroup ? (conversation.title || conversation.name || '') : '',
            isInitiator: true,
            /*
             * An optimistic roster so the window has something to draw while it
             * rings. Replaced wholesale by the server's the moment anyone
             * answers — this is a placeholder, not a source of truth.
             */
            participants: [
                { userId: myUserId(), person: myPerson(), state: 'joined', joinSeq: 1, isSelf: true, isInitiator: true },
                ...(!isGroup && other?._id
                    ? [{ userId: String(other._id), person: other, state: 'ringing', joinSeq: 0 }]
                    : [])
            ]
        });

        // Asked here, at a genuine user action, rather than on page load —
        // see attention.js. The answer is not awaited into the call path.
        requestNotificationPermission();

        try {
            await ensureLocalStream(kind);
        } catch (err) {
            reportCallIssue(null, 'media_error', err.message, { fatal: true, detail: { name: err.name, kind, when: 'start' } });
            setError(err.message);
            teardown();
            return;
        }

        getSocket()?.emit('call:start', {
            conversationId: String(conversation._id),
            kind,
            ...(userIds?.length ? { userIds: userIds.map(String) } : {})
        });
    }, [ensureLocalStream, teardown, setCall]);

    /** Answer a ringing call. */
    const acceptCall = useCallback(async () => {
        const current = callRef.current;
        if (current.status !== 'incoming') return;

        clearAttention();
        setView('window');
        setCall((prev) => ({ ...prev, status: 'connecting' }));
        requestNotificationPermission();

        try {
            await ensureLocalStream(current.kind);
            await publishLocal();
        } catch (err) {
            reportCallIssue(current.callId, 'media_error', err.message, { fatal: true, detail: { name: err.name, kind: current.kind, when: 'accept' } });
            setError(err.message);
            getSocket()?.emit('call:decline', { callId: current.callId });
            teardown();
            return;
        }

        /*
         * Told to the server only once the microphone is actually open, so that
         * everyone else's "connecting" means something: the media server token
         * that comes back is used at once, and the microphone is published as
         * soon as the room is joined.
         */
        getSocket()?.emit('call:accept', { callId: current.callId });
    }, [ensureLocalStream, publishLocal, teardown, setCall]);

    /**
     * Join a call already running in a group, from its "Join" banner.
     *
     * The same order as answering: the microphone is opened first, and only
     * then is the server told, so the people already talking have something to
     * connect to when they offer. The server adds us to the roster and answers
     * with call:claimed and call:roster exactly as it does for an accept.
     *
     * `asSecondDevice` is "Join from here" on a call we are already on from
     * another device. We come in muted: two open microphones in the same room
     * echo for everybody else.
     */
    const joinCall = useCallback(async ({
        callId, kind = 'audio', conversationId, conversationTitle = '', isGroup = true, asSecondDevice = false
    }) => {
        if (callRef.current.status !== 'idle' || !callId) return;

        setError('');
        setNotice('');
        setView('window');
        setCall({
            ...IDLE,
            status: 'connecting',
            callId: String(callId),
            conversationId: conversationId ? String(conversationId) : null,
            kind,
            isGroup,
            conversationTitle
        });
        requestNotificationPermission();

        try {
            const stream = await ensureLocalStream(kind);
            if (asSecondDevice) {
                micOnRef.current = false;
                setMicOn(false);
                for (const track of stream.getAudioTracks()) track.enabled = false;
                announceMediaRef.current = true;
            }
            await publishLocal();
        } catch (err) {
            setError(err.message);
            teardown();
            return;
        }

        getSocket()?.emit('call:join', { callId: String(callId) });
    }, [ensureLocalStream, publishLocal, teardown, setCall]);

    const declineCall = useCallback(() => {
        const current = callRef.current;
        if (!current.callId) return;
        getSocket()?.emit('call:decline', { callId: current.callId });
        teardown();
    }, [teardown]);

    /** Hang up — whether it is ringing out or in progress. */
    const hangUp = useCallback(() => {
        const current = callRef.current;
        if (!current.callId) {
            // Ringing out, but the server has not issued a call id yet.
            teardown();
            return;
        }

        const event = current.status === 'outgoing' ? 'call:cancel' : 'call:leave';
        getSocket()?.emit(event, { callId: current.callId });
        teardown();
    }, [teardown]);

    /** Bring more people into a call that is already running. */
    const inviteToCall = useCallback((userIds) => {
        const current = callRef.current;
        if (!current.callId || !userIds?.length) return;
        getSocket()?.emit('call:invite', {
            callId: current.callId,
            userIds: userIds.map(String)
        });
    }, []);

    /*
     * Mute is `track.enabled = false`, not a removed track: the connections stay
     * up and carry silence, so unmuting is instant and nobody has to
     * renegotiate. The published audio track is that same track object, so
     * setting it once on the stream is all it takes.
     *
     * The socket message is not decoration. A muted track still sends, so the
     * far end cannot tell "muted" from "not talking" by listening; without
     * being told, their tile would show no mute icon at all.
     */
    /**
     * Tell the others what this tab is sending.
     *
     * Always all three, never just the one that changed. A message carrying only
     * `screenOn` leaves the server defaulting the rest, which would un-mute a
     * muted person's badge on everybody else's screen the moment they shared.
     */
    const publishMediaState = useCallback((patch = {}) => {
        const callId = callRef.current.callId;
        if (!callId) return;
        getSocket()?.emit('call:media', {
            callId,
            micOn: micOnRef.current,
            camOn: Boolean(localStreamRef.current?.getVideoTracks()[0]?.enabled),
            screenOn: Boolean(screenStreamRef.current),
            ...patch
        });
    }, []);

    const toggleMic = useCallback(() => {
        setMicOn((on) => {
            const next = !on;
            micOnRef.current = next;
            for (const track of localStreamRef.current?.getAudioTracks() || []) {
                track.enabled = next;
            }
            publishMediaState({ micOn: next });
            return next;
        });
    }, [publishMediaState]);

    /**
     * Share a screen, a window or a tab.
     *
     * The capture is started BEFORE the server is asked, and the order matters:
     * Chrome only opens the picker from inside the task of a user gesture, and a
     * socket round trip breaks that chain — the picker would either refuse to
     * open or appear a beat late, after the click that asked for it. The cost of
     * losing the race for the one shared slot is a picker that opens and closes
     * again, which happens approximately never.
     */
    const startScreenShare = useCallback(async () => {
        if (screenStreamRef.current) return;
        if (!callRef.current.callId) return;

        let stream;
        try {
            stream = await getDisplayStream();
        } catch (err) {
            if (!err.cancelled) setError(err.message);
            return;
        }

        // The call ended while the picker was open.
        if (!callRef.current.callId) {
            stopStream(stream);
            return;
        }

        screenStreamRef.current = stream;
        setScreenStream(stream);

        /*
         * Chrome's own "Stop sharing" bar is the control most people will
         * actually press, and it does not go through ours. Both routes have to
         * land in the same teardown, or the capture is left running with the
         * browser's indicator lit and nothing in the UI able to turn it off.
         */
        const [track] = stream.getVideoTracks();
        if (track) {
            track.onended = () => {
                // eslint-disable-next-line no-use-before-define
                stopScreenShareRef.current?.();
            };
        }

        await mediaRoom().setLocal({ screen: track || null });
        publishMediaState({ screenOn: true });
    }, [mediaRoom, publishMediaState]);

    const stopScreenShare = useCallback(async () => {
        const stream = screenStreamRef.current;
        if (!stream) return;

        const [track] = stream.getVideoTracks();
        if (track) track.onended = null;

        screenStreamRef.current = null;
        setScreenStream(null);

        await roomRef.current?.setLocal({ screen: null });
        stopStream(stream);
        publishMediaState({ screenOn: false });
    }, [publishMediaState]);

    // The native stop bar fires from a track callback created before
    // stopScreenShare exists; this is the knot that ties them.
    const stopScreenShareRef = useRef(stopScreenShare);
    useEffect(() => { stopScreenShareRef.current = stopScreenShare; }, [stopScreenShare]);

    const toggleScreenShare = useCallback(() => (
        screenStreamRef.current ? stopScreenShare() : startScreenShare()
    ), [startScreenShare, stopScreenShare]);

    const minimizeCall = useCallback(() => setView('minimized'), []);
    const expandCall = useCallback(() => setView('window'), []);

    /* ---------------------------------------------------------------- *
     * Socket subscriptions
     * ---------------------------------------------------------------- */

    useEffect(() => {
        unlockAudio();
        // Warm the cache so maxParticipants is known before anybody presses
        // call, and the chat header can grey out an oversized group.
        getIceConfig().then((cfg) => {
            if (cfg?.maxParticipants) {
                setCall((prev) => (prev.status === 'idle'
                    ? { ...prev, maxParticipants: cfg.maxParticipants }
                    : prev));
            }
        }).catch(() => { /* the default of twenty stands */ });
    }, [setCall]);

    useEffect(() => {
        const offs = [];

        /* --- somebody is calling us --- */
        offs.push(onSocket('call:incoming', (payload) => {
            // Already busy. The server refuses a second call, but a second
            // *incoming* one can still arrive in the gap.
            if (callRef.current.status !== 'idle') {
                getSocket()?.emit('call:decline', { callId: payload.callId });
                return;
            }

            setCall({
                ...IDLE,
                status: 'incoming',
                callId: payload.callId,
                conversationId: payload.conversationId,
                kind: payload.kind,
                isGroup: Boolean(payload.isGroup),
                conversationTitle: payload.conversationTitle || '',
                from: payload.from,
                maxParticipants: payload.maxParticipants || 20,
                isInitiator: false,
                participants: (payload.onCall || []).map((person, i) => ({
                    userId: String(person._id),
                    person,
                    state: 'joined',
                    joinSeq: i + 1
                }))
            });

            const what = payload.kind === 'video' ? 'video call' : 'call';
            const where = payload.isGroup && payload.conversationTitle
                ? ` · ${payload.conversationTitle}`
                : '';
            startRinging(payload.callId);
            flashTitle(`Incoming ${what}`);
            notify({
                title: `Incoming ${what}${where}`,
                body: payload.isGroup
                    ? `${payload.from?.name || 'Someone'} is calling the group`
                    : `${payload.from?.name || 'Someone'} is calling you`,
                tag: `call-${payload.callId}`
            });
        }));

        /* --- our own call is now ringing at the other end --- */
        offs.push(onSocket('call:ringing', (payload) => {
            /*
             * This reaches every tab we have open, because emitToUser targets
             * the whole user room. Only the tab that actually placed the call
             * holds the microphone, so the others ignore it rather than
             * rendering a call they cannot participate in.
             */
            if (payload.placedBySocketId !== getSocket()?.id) return;

            /*
             * Hanging up in the moment between pressing call and the server
             * answering leaves us with no call id to cancel, so the callees
             * would keep ringing for the full thirty-five seconds for someone
             * who is no longer there. The id arrives here — cancel it now.
             */
            if (callRef.current.status !== 'outgoing') {
                getSocket()?.emit('call:cancel', { callId: payload.callId });
                return;
            }

            setCall((prev) => (prev.status === 'outgoing'
                ? {
                    ...prev,
                    callId: payload.callId,
                    maxParticipants: payload.maxParticipants || prev.maxParticipants
                }
                : prev));
        }));

        /* --- the authoritative roster --- */
        offs.push(onSocket('call:roster', (payload) => {
            const current = callRef.current;
            if (current.callId !== payload.callId) return;
            applyRoster(payload);
            // Whoever is still being rung gets rosters too, so that their
            // incoming card can say who is already in there. They are not in
            // the call yet and must not start connecting to anybody.
            if (current.status === 'incoming') return;
            publishLocal();
            // Seated, muted, from a second device: the others' tiles show it.
            if (announceMediaRef.current) {
                announceMediaRef.current = false;
                publishMediaState();
            }
        }));

        /* --- the call's chat moved: a group was made, or the pair changed --- */
        offs.push(onSocket('call:chat', ({ callId, conversationId, isCallGroup }) => {
            if (callRef.current.callId !== callId) return;
            setCall((prev) => ({
                ...prev,
                chatConversationId: conversationId,
                chatIsCallGroup: Boolean(isCallGroup)
            }));
        }));

        /* --- somebody joined: a tile now, their media comes from the media server --- */
        offs.push(onSocket('call:participant-joined', async ({ callId, userId, person, joinSeq }) => {
            const current = callRef.current;
            if (current.callId !== callId) return;
            console.log(`[call] participant-joined ${String(userId).slice(-6)}`);

            setCall((prev) => {
                const without = prev.participants.filter((p) => p.userId !== String(userId));
                return {
                    ...prev,
                    status: prev.status === 'outgoing' ? 'connecting' : prev.status,
                    participants: [...without, {
                        userId: String(userId), person, state: 'joined', joinSeq,
                        isSelf: String(userId) === meRef.current, connectionState: '', micOn: true, camOn: false
                    }].sort((a, b) => (a.joinSeq || Infinity) - (b.joinSeq || Infinity))
                };
            });

        }));

        /* --- somebody left; the call carries on without them --- */
        offs.push(onSocket('call:participant-left', ({ callId, userId, reason }) => {
            if (callRef.current.callId !== callId) return;

            dropMedia(userId);

            setCall((prev) => {
                const gone = prev.participants.find((p) => p.userId === String(userId));
                const rest = prev.participants.filter((p) => p.userId !== String(userId));
                if (gone?.person?.name && reason !== 'left') {
                    const word = reason === 'declined' ? 'declined' : 'did not answer';
                    setNotice(`${gone.person.name} ${word}`);
                }
                return { ...prev, participants: rest };
            });
        }));

        /* --- somebody muted, or turned a camera on --- */
        offs.push(onSocket('call:media', ({ callId, userId, micOn: theirMic, camOn, screenOn }) => {
            if (callRef.current.callId !== callId) return;
            /*
             * The flag only. The screen STREAM is never touched here, however
             * tempting it is to clear it when somebody stops sharing.
             *
             * A remote screen arrives exactly once — ontrack fires when the
             * connection is first negotiated, and every share after that is
             * replaceTrack on the same slot, which never fires it again. A stream
             * deleted here is therefore gone for the life of the connection. This
             * handler used to delete it on `screenOn: false`, which every media
             * message carries for anyone not sharing — including the one sent when
             * they mute — and from then on their shares turned the flag on over
             * nothing, and nobody could see them.
             *
             * Nothing stale can show as a result: CallStage renders the spotlight
             * only while this flag is set, so a stopped share is unmounted, and
             * the next one remounts onto the same track once frames flow again.
             */
            patchParticipant(userId, {
                micOn: theirMic !== false,
                camOn: Boolean(camOn),
                sharingScreen: Boolean(screenOn)
            });
        }));

        /*
         * Somebody else got there first.
         *
         * A notice rather than an error: wanting to share while a colleague is
         * already sharing is not the call going wrong, and tearing it down for
         * that would be absurd.
         */
        offs.push(onSocket('call:share-denied', ({ callId, by }) => {
            if (callRef.current.callId !== callId) return;
            setNotice(`${by?.name || 'Someone'} is sharing their screen. Ask them to stop first.`);
            stopScreenShareRef.current?.();
        }));

        /* --- what happened to the people we tried to add --- */
        offs.push(onSocket('call:invite-result', ({ callId, results }) => {
            if (callRef.current.callId !== callId) return;

            const words = {
                busy: 'is on another call',
                offline: 'is not online',
                'not-found': 'could not be found',
                'already-in': 'is already on this call'
            };
            const problems = (results || [])
                .filter((r) => r.status !== 'ringing')
                .map((r) => `${r.name || 'Someone'} ${words[r.status] || 'could not be added'}`);

            // A notice, never an error: an invitation that could not be
            // delivered must not look like the call going wrong.
            if (problems.length) setNotice(problems.join(' · '));
        }));

        /* --- answered or declined on another tab --- */
        offs.push(onSocket('call:claimed', ({ callId, socketId }) => {
            if (callRef.current.callId !== callId) return;
            if (socketId === getSocket()?.id) return;

            // Another of this user's tabs took it. Fall silent and stand down;
            // this tab is not in the call.
            teardown();
        }));

        /* --- over --- */
        offs.push(onSocket('call:ended', ({ callId, reason }) => {
            if (callRef.current.callId !== callId) return;

            const wasRinging = callRef.current.status === 'incoming';
            const messages = {
                declined: 'Call declined',
                // The same reason reads differently at each end: the caller
                // failed to reach anybody, the callee missed something.
                missed: wasRinging ? 'Missed call' : 'No answer',
                cancelled: 'Call cancelled',
                failed: 'Call failed',
                completed: 'Call ended'
            };
            if (messages[reason]) setError(messages[reason]);

            teardown();
        }));

        /* --- the server refused something --- */
        offs.push(onSocket('call:error', ({ code, message, callId, fatal }) => {
            const current = callRef.current;

            /*
             * Only a fatal error about THIS call ends it.
             *
             * This used to tear down unconditionally, which was right when the
             * only failures were "could not start" and "that call has ended".
             * Now that a live call can be told one invitee is busy, hanging up
             * on the four people already talking is far worse than the problem
             * being reported.
             */
            const aboutThisCall = !callId || callId === current.callId;
            if (fatal === false || !aboutThisCall) {
                if (aboutThisCall) setNotice(message || 'That did not work.');
                return;
            }

            /*
             * "That call has ended" while we were recovering media or a socket is the
             * other side hanging up just ahead of the call:ended we are about to get.
             * Nothing went wrong, so leave quietly instead of flashing an error.
             */
            const recovering = !!mediaRecoveryRef.current || current.status === 'reconnecting';
            if (!(code === 'GONE' && recovering)) setError(message || 'Call failed.');
            teardown();
        }));

        /*
         * Our way into the call's media: a LiveKit token for this seat, sent by
         * the server whenever we take one (placing, answering, joining, coming
         * back). Connecting publishes whatever we already have open.
         */
        offs.push(onSocket('call:livekit', async ({ callId, url, token, identity }) => {
            if (callRef.current.callId !== callId) return;
            const m = mediaRoom();
            try {
                await m.connect(url, token, identity);
                await publishLocal();
                if (mediaRecoveryRef.current && roomRef.current === m) {
                    console.log('[call] media server connection restored');
                    clearTimeout(mediaRecoveryRef.current.timer);
                    mediaRecoveryRef.current = null;
                    setCall((prev) => (prev.status === 'reconnecting' ? { ...prev, status: 'active' } : prev));
                }
            } catch (err) {
                // Hung up while connecting: the room was closed under it. Not news.
                if (roomRef.current !== m || callRef.current.callId !== callId) return;
                console.error('[call] media server connection failed:', err.message);
                // Not the end of the call - recoverMedia keeps trying until its deadline.
                recoverMediaRef.current();
            }
        }));

        /*
         * Losing the socket puts the call on hold, and it has to be said out loud.
         *
         * The server holds our seat for RECONNECT_GRACE_MS and we resume it with
         * call:resume when the socket is back (below). Past that the seat is
         * gone, and the way back is the group's "Join" banner (call:join).
         */
        offs.push(onSocket('disconnect', () => {
            if (!IN_CALL.includes(callRef.current.status)) return;

            /*
             * The network went away; the call has not.
             *
             * This ended the call outright, so a dropped Wi-Fi connection hung
             * up on everybody in it. The server holds the seat for thirty
             * seconds and so does this. The media server connection is
             * separate and survives a socket drop on its own - often the audio
             * never stops at all.
             */
            setCall((prev) => ({ ...prev, status: 'reconnecting' }));

            clearTimeout(reconnectTimer.current);
            reconnectTimer.current = setTimeout(() => {
                if (callRef.current.status !== 'reconnecting') return;
                reportCallIssue(callRef.current.callId, 'reconnect_timeout', 'Connection lost and did not return in time.', { fatal: true });
                setError('Connection lost. The call has ended.');
                teardownRef.current();
            }, RECONNECT_TIMEOUT_MS);
        }));

        /*
         * Back after a drop: claim the seat again.
         *
         * Only a client that believes it is still in a call may assert this,
         * and it is what lifts the server's deadline and has the others
         * discard the peers they held and offer afresh.
         */
        offs.push(onSocket('connect', () => {
            const held = callRef.current;
            if (held.status !== 'reconnecting' || !held.callId) return;
            clearTimeout(reconnectTimer.current);
            // Our seat, so a second device takes back its own and not the other's.
            getSocket()?.emit('call:resume', { callId: held.callId, seatId: meRef.current });
            setCall((prev) => (prev.status === 'reconnecting' ? { ...prev, status: 'connecting' } : prev));
        }));

        return () => { for (const off of offs) off(); };
    }, [applyRoster, dropMedia, mediaRoom, patchParticipant, publishLocal, publishMediaState, teardown, setCall]);

    /*
     * Stop the tab flashing once the person actually looks at it. Nothing else
     * in the app listens for this, and without it a title keeps blinking at
     * someone who is already reading the screen.
     */
    useEffect(() => {
        const onVisible = () => { if (!document.hidden) stopFlashTitle(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);

    /* ---------------------------------------------------------------- *
     * Call sounds
     * ---------------------------------------------------------------- */

    /*
     * Ringback — the caller hears the other phone ringing.
     *
     * Keyed off the call id arriving, not off pressing Call: the id comes back
     * with `call:ringing`, the moment the server has actually rung the other
     * side. Before that the caller may still be looking at the microphone
     * permission prompt, and a ringing sound would claim something that is not
     * happening yet. Stops the moment anybody answers.
     */
    const ringingOut = call.status === 'outgoing' && Boolean(call.callId);
    useEffect(() => {
        if (!ringingOut) return undefined;
        startRingback();
        return () => stopRingback();
    }, [ringingOut]);

    /*
     * The disconnect beep, when a call that was ringing out or in progress
     * ends — hung up by either side, declined, unanswered or dropped.
     *
     * Not for a call that only rang here: someone who pressed Decline, or
     * whose other tab answered, does not need telling that it is over.
     */
    const lastCallRef = useRef(IDLE);
    useEffect(() => {
        const last = lastCallRef.current;
        lastCallRef.current = call;
        if (call.status === 'idle' && last.callId && IN_CALL.includes(last.status)) {
            playDisconnected();
        }
    }, [call]);

    /* --- errors and notices are transient --- */
    useEffect(() => {
        if (!error) return undefined;
        const timer = setTimeout(() => setError(''), 5000);
        return () => clearTimeout(timer);
    }, [error]);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = setTimeout(() => setNotice(''), 5000);
        return () => clearTimeout(timer);
    }, [notice]);

    /* ---------------------------------------------------------------- *
     * Leaving the page
     * ---------------------------------------------------------------- */

    const inCall = IN_CALL.includes(call.status);

    /*
     * The output device list, refreshed for as long as a call is up.
     *
     * Labels are blank without a granted getUserMedia permission, which is
     * exactly the gap between "the call hasn't started" and "it has" — so
     * this deliberately doesn't run outside a call. `devicechange` fires when
     * a Bluetooth headset connects or disconnects mid-call, which is the
     * moment the list actually needs updating.
     */
    const refreshAudioOutputs = useCallback(async () => {
        if (!audioOutputSupported) return;
        try {
            setAudioOutputs(await listAudioOutputs());
        } catch { /* enumerateDevices can refuse; the picker just stays empty */ }
    }, [audioOutputSupported]);

    useEffect(() => {
        if (!audioOutputSupported || !inCall) return undefined;
        refreshAudioOutputs();
        navigator.mediaDevices.addEventListener('devicechange', refreshAudioOutputs);
        return () => navigator.mediaDevices.removeEventListener('devicechange', refreshAudioOutputs);
    }, [inCall, audioOutputSupported, refreshAudioOutputs]);

    /**
     * Send the call's audio out of a specific device instead of the
     * browser's default — the earpiece/speaker/headset choice a native phone
     * app gives during a call. Applied to every remote `<audio>` element by
     * CallAudioSinks, and remembered across calls the same as the noise
     * suppression preference.
     */
    const setAudioOutput = useCallback((deviceId) => {
        setAudioOutputIdState(deviceId);
        try {
            if (deviceId) localStorage.setItem(AUDIO_OUTPUT_KEY, deviceId);
            else localStorage.removeItem(AUDIO_OUTPUT_KEY);
        } catch { /* private mode */ }
    }, []);

    /*
     * No reloading mid-call. The call lives in this page, so a reload is a
     * hang-up.
     *
     * The keyboard shortcuts can be stopped outright. The browser's own reload
     * button, closing the tab and typing a new address cannot — no page is
     * allowed to block those — so for them the browser is asked to confirm
     * with its standard "Leave site?" prompt.
     */
    useEffect(() => {
        if (!inCall) return undefined;

        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        const onKeyDown = (event) => {
            const key = (event.key || '').toLowerCase();
            const isReload = key === 'f5' || ((event.ctrlKey || event.metaKey) && key === 'r');
            if (!isReload) return;
            event.preventDefault();
            setError('Reloading is disabled during a call. End the call first.');
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [inCall]);

    /*
     * Hang up cleanly when the page really goes away.
     *
     * pagehide, not beforeunload: beforeunload fires *before* the "Leave
     * site?" prompt above, so hanging up there would end the call even for
     * someone who then pressed Cancel.
     */
    useEffect(() => {
        const onPageHide = () => {
            const current = callRef.current;
            if (current.callId && current.status !== 'idle') {
                const event = current.status === 'outgoing' ? 'call:cancel' : 'call:leave';
                getSocket()?.emit(event, { callId: current.callId });
            }
            stopRinging();
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, []);

    /*
     * And when the layout unmounts — logging out navigates to /login, outside
     * DashboardLayout. Without this the microphone would stay open, recording
     * indicator and all, on the login screen.
     */
    useEffect(() => () => {
        const current = callRef.current;
        if (current.callId && current.status !== 'idle') {
            const event = current.status === 'outgoing' ? 'call:cancel' : 'call:leave';
            getSocket()?.emit(event, { callId: current.callId });
        }
        clearAttention();
        roomRef.current?.closeAll();
        roomRef.current = null;
        stopStream(localStreamRef.current);
        localStreamRef.current = null;
    }, []);

    /* ---------------------------------------------------------------- *
     * What the UI reads
     * ---------------------------------------------------------------- */

    const others = useMemo(
        () => call.participants.filter((p) => p.userId !== me),
        [call.participants, me]
    );

    /**
     * The one name at the top of the window.
     *
     * A group call is named after its group; a two-person call after the other
     * person; and a call that grew out of a direct chat after however many
     * people ended up in it, because "Rahul" is the wrong title for a call with
     * Rahul and two others in it.
     */
    const headline = useMemo(() => {
        if (call.isGroup && call.conversationTitle) return call.conversationTitle;
        if (call.status === 'incoming') return call.from?.name || 'Unknown caller';
        if (others.length === 1) return others[0].person?.name || 'Call';
        if (others.length > 1) {
            const first = others[0]?.person?.name || 'Someone';
            return `${first} and ${others.length - 1} other${others.length > 2 ? 's' : ''}`;
        }
        return 'Call';
    }, [call.isGroup, call.conversationTitle, call.status, call.from, others]);

    /**
     * How loudly each remote participant is talking, 0 to 1.
     *
     * A function rather than state on purpose: this is polled several times a
     * second, and putting it in state would re-render the whole call tree at
     * that rate to move one border.
     */
    const levels = useCallback(() => roomRef.current?.levels() || {}, []);

    const value = useMemo(() => ({
        call,
        micOn,
        error,
        notice,
        remoteMedia,
        localStream,
        screenStream,
        sharingScreen: Boolean(screenStream),
        toggleScreenShare,
        levels,
        view,
        me,
        others,
        headline,
        startCall,
        acceptCall,
        joinCall,
        declineCall,
        hangUp,
        inviteToCall,
        toggleMic,
        noiseSuppressionOn,
        noiseSuppressionSupported,
        toggleNoiseSuppression,
        audioOutputId,
        audioOutputs,
        audioOutputSupported,
        setAudioOutput,
        minimizeCall,
        expandCall,
        setNotice,
        // What the chat header needs to know without reading the whole state.
        isBusy: call.status !== 'idle',
        maxParticipants: call.maxParticipants
    }), [call, micOn, error, notice, remoteMedia, localStream, screenStream,
        toggleScreenShare, levels, view, me, others, headline, startCall,
        acceptCall, joinCall, declineCall, hangUp, inviteToCall, toggleMic,
        noiseSuppressionOn, noiseSuppressionSupported, toggleNoiseSuppression,
        audioOutputId, audioOutputs, audioOutputSupported, setAudioOutput,
        minimizeCall, expandCall]);

    const showCorner = call.status === 'incoming' || (inCall && view === 'minimized');

    return (
        <CallContext.Provider value={value}>
            {children}

            {/* The audio sinks live in the provider, not in the call UI: the
                window unmounts when the call is minimised, and a sink inside it
                would make minimising a call the same as muting everybody. */}
            <CallAudioSinks media={remoteMedia} sinkId={audioOutputId} />

            {inCall && view === 'window' && <CallWindow />}

            {(showCorner || error || notice) && (
                <div className="call-corner">
                    {showCorner && <CallWidget />}
                    {error && <div className="call-toast" role="status">{error}</div>}
                    {notice && !error && (
                        <div className="call-toast is-notice" role="status">{notice}</div>
                    )}
                </div>
            )}
        </CallContext.Provider>
    );
};

export default CallContext;
